import { RAY_BAN, RAY_BAN_LOGO } from '@/scripts/constants/store'
import { getBestBetweenTwoPromos, sortPromos } from '@/scripts/libs/sort-promos'
import { Product, WcsFrameAdvisorProduct } from '@/types/frame-genius'
import { IS_FROM_QUERY_PARAM } from '@/scripts/constants/frame-advisor'
import { REF } from '@/scripts/constants/parameters'
import { formatPriceForPDP } from '@/scripts/libs/price'
import { Y } from '@/scripts/constants/booleans'
import { SUN, SUNGLASSES, EYEGLASSES } from '@/scripts/constants/pdp'
import { WcsLocales } from '@/scripts/libs/wcs/types/locales'
import { WcsStoreId } from '@/scripts/libs/wcs/types/store-id'
import { MocoAvailability } from '@/scripts/interfaces/normalizer'
import PDP from '@/scripts/libs/pdp'

let mocoAvailabilitySupport = {} as MocoAvailability

export const adjustLocale = (locale: string) => {
  switch (locale) {
    case WcsLocales.EN_NL:
      return WcsLocales.NL_NL
    case WcsLocales.EN_PL:
      return WcsLocales.PL_PL
    case WcsLocales.EN_GB:
      return WcsLocales.EN_UK
    default:
      return locale
  }
}

export const getLocaleByStoreId = (storeId: string, locale: string) => {
  switch (storeId) {
    case WcsStoreId.LATAM:
      return WcsLocales.ES_MX
    case WcsStoreId.REST_OF_EUROPE:
      return WcsLocales.EN_EU
    case WcsStoreId.ASEAN:
    case WcsStoreId.TURKEY:
    case WcsStoreId.REST_OF_WORLD:
    case WcsStoreId.MIDDLE_EAST:
      return WcsLocales.EN_US
    default:
      return adjustLocale(locale)
  }
}

export const mapApiStore = (locale: string) => {
  switch (locale) {
    case WcsLocales.EN_US:
    case WcsLocales.EN_CA:
    case WcsLocales.FR_CA:
      return 'USA'
    case WcsLocales.EN_GB:
      return 'UK'
    case WcsLocales.EN_SE:
      return 'SW'
    default: {
      const splitLocale = locale.split('-')
      return splitLocale[1]
    }
  }
}

export const openRoxLensPanel = (config: any) => {
  window.triggerVueMethod('showRoxPanel', config)
}

export const normalizeFrameAdvisorKeysProduct = (
  product: WcsFrameAdvisorProduct
): WcsFrameAdvisorProduct => {
  return {
    afterpayEnabled: product.afterpayEnabled,
    afterpayInstallments: product.afterpayInstallments,
    category: product.category,
    catEntryId: product.catEntryId || product.roxFrameCatentryId,
    clen: product.clen,
    color: product.color,
    colorCategory: product.colorCategory,
    currency: product.currency,
    frameColor: product.frameColor,
    imageUrl: product.imageUrl,
    inWishlist: product.inWishlist,
    klarnaEnabled: product.klarnaEnabled,
    klarnaInstallments: product.klarnaInstallments,
    lensColor: product.lensColor,
    moco: product.moco,
    model: product.model,
    name: product.name,
    prices: {
      currency: product.currency,
      currencySymbol: product.prices.currencySymbol,
      listPrice: product.prices.listPrice,
      priceFormat: product.prices.priceFormat,
    },
    productName: product.productName,
    promotions: product.promotions,
    roxFrameCatentryId: product.roxFrameCatentryId,
    rxAvailable: product.rxAvailable,
    size: product.size,
    upc: product.upc,
    url: product.url,
    prescriptionThresholds: product.prescriptionThresholds,
  }
}

export const normalizeProductForWhiteLabel = async (
  product: WcsFrameAdvisorProduct
): Promise<Product> => {
  const productPrices = product.prices
  const price = formatPriceForPDP(
    productPrices.priceFormat,
    productPrices.currencySymbol,
    productPrices.listPrice
  )
  const promotions = sortPromos(product.promotions)
  const currentPromo = getBestBetweenTwoPromos(promotions)
  const discountedPrice = currentPromo ? currentPromo.priceLabel : undefined
  const match = currentPromo ? currentPromo.badge?.match(/\b(\d+)%/) : undefined
  const percentDiscount = match ? match[1] : undefined
  const URL = product.url ? `${product.url}?${REF}=${IS_FROM_QUERY_PARAM}` : ''

  const availabilityCall =
    product.rxAvailable && !mocoAvailabilitySupport[product?.moco]?.availability
      ? await PDP.getMoCoAvailability(getModelForRoxPanel(product.moco))
      : { data: {} }
  mocoAvailabilitySupport = { ...mocoAvailabilitySupport, ...availabilityCall?.data }
  const isAvailableForRoxPanel = !!mocoAvailabilitySupport[product?.moco]?.availability

  return {
    brandlogoUrl: RAY_BAN_LOGO,
    brandName: RAY_BAN,
    frameColor: product.frameColor,
    imageUrl: product.imageUrl,
    inWishlist: product.inWishlist,
    lensColor: product.lensColor,
    percentDiscount,
    price: discountedPrice ? discountedPrice : price,
    priceFull: discountedPrice ? price : '',
    productName: product.name,
    rxAvailable: isAvailableForRoxPanel,
    upc: product.upc,
    url: URL,
    isPlano: true,
  }
}

export const mapRxcConfig = (product: WcsFrameAdvisorProduct) => {
  const listPrice = product.prices.listPrice
  const promotions = sortPromos(product.promotions)
  const currentPromo = getBestBetweenTwoPromos(promotions)
  return {
    frame: {
      catEntryId: product.catEntryId,
      roxFrameCatentryId: product.roxFrameCatentryId,
      name: product.name,
      upc: product.upc,
      listPrice,
      offerPrice: currentPromo?.price,
      category: product.category === SUN ? SUNGLASSES : EYEGLASSES,
      brand: RAY_BAN,
      imageUrl: product.imageUrl,
      rxValues: product.prescriptionThresholds,
      size: product.size,
      model: getModelForRoxPanel(product.moco),
      color: getColorForRoxPanel(product.moco),
    },
    brand: 'rayban',
    lens: {
      colorCategory: product.colorCategory,
      clen: product.clen,
    },
    klarnaEnabled: product.klarnaEnabled === 'true',
    klarnaInstallments: +product.klarnaInstallments,
    afterpayEnabled: product.afterpayEnabled === Y,
    afterpayInstallments: +product.afterpayInstallments,
  }
}

export const getModelForRoxPanel = (moco: string): string => {
  return moco?.split('__')[0]
}

export const getColorForRoxPanel = (moco: string): string => {
  return moco?.split('__')[1]?.split(' ')[0]
}
